import { useMemo } from "react";
import {
  SharedPDFViewer,
  type SharedBoundingBox,
} from "@capacit-com/lara-law-shared-ui/pdf-viewer";
import { SPREADSHEET_ZOOM_RANGE } from "./spreadsheetZoom";
import DocumentReference, {
  BoundingBox,
} from "@/models/conversation/document";
import { WorkspaceDocument } from "@/models/workspace/Workspace";
import {
  Legislation,
  DocumentCategory,
} from "@/models/legislation/Legislation";
import { useTheme } from "@/hooks/useTheme";

// ---------------------------------------------------------------------------
// Public API (kept stable so existing callsites — WorkspaceDocumentView and
// UnifiedLegislationPDFView — don't have to change.)
// ---------------------------------------------------------------------------

type Props = {
  uri: string;
  document: WorkspaceDocument | Legislation | DocumentReference;
  documentCategory: DocumentCategory;
  boundingBoxes?: BoundingBox[];
  /** A sheet of an Excel workbook, printed by the API at full size (LAR-490). */
  isSpreadsheetPreview?: boolean;
};

// ---------------------------------------------------------------------------
// Helpers — translate web-app's domain model into the shared component API.
// ---------------------------------------------------------------------------

const isDocumentReference = (
  doc: WorkspaceDocument | Legislation | DocumentReference
): doc is DocumentReference =>
  (doc as DocumentReference).documentId !== undefined;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isLegislation = (doc: any): doc is Legislation =>
  (doc as Legislation).title !== undefined;

const getDisplayName = (
  doc: WorkspaceDocument | Legislation | DocumentReference
): string => {
  if (isLegislation(doc)) return doc.title;
  if (isDocumentReference(doc)) return doc.documentTitle;
  return doc.name;
};

const formatDateForFilename = (input: Date | string): string => {
  const d = new Date(input);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const EmbedPDFViewer: React.FC<Props> = ({
  uri,
  document,
  boundingBoxes,
  documentCategory,
  isSpreadsheetPreview = false,
}) => {
  const { useQueryTheme } = useTheme();
  const fetchedTheme = useQueryTheme();
  const themePreference = fetchedTheme.data === "dark" ? "dark" : "light";

  const documentId = isDocumentReference(document)
    ? document.documentId
    : isLegislation(document)
      ? document.internalId
      : document.id;

  const displayName = getDisplayName(document);

  const defaultFileName = useMemo((): string => {
    if (documentCategory === DocumentCategory.Workspace) {
      const doc = document as WorkspaceDocument;
      const active =
        doc.revisions?.find(
          (r) => r.revisionVersion === doc.activeRevision
        ) ?? doc.revisions?.[doc.revisions.length - 1];
      const stamp = active?.updatedAt ?? new Date();
      const base = (doc.name || "document").replace(/[^\w-]+/g, "_");
      return `${base}_${formatDateForFilename(stamp)}.pdf`;
    }
    return `${displayName.replace(/[^\w-]+/g, "_")}.pdf`;
  }, [documentCategory, document, displayName]);

  // BoundingBox is already shape-compatible with SharedBoundingBox; just keep
  // the reference stable so the shared component doesn't re-sync needlessly.
  const sharedBoundingBoxes = useMemo<SharedBoundingBox[] | undefined>(
    () => boundingBoxes,
    [boundingBoxes]
  );

  return (
    <SharedPDFViewer
      url={uri}
      documentId={documentId}
      themePreference={themePreference}
      boundingBoxes={sharedBoundingBoxes}
      defaultFileName={defaultFileName}
      initialZoomRange={
        isSpreadsheetPreview ? SPREADSHEET_ZOOM_RANGE : undefined
      }
      className="flex-1 min-h-0 h-full w-full shadow-md border rounded-md overflow-hidden"
    />
  );
};

export default EmbedPDFViewer;
